import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider
} from '@mui/material';
import {
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CaretLeftOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined
} from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from '@tanstack/react-query';
import { getReport } from 'services/report/getReport';

const StudentProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const student = location.state?.student;
  const coachId = localStorage.getItem('userid');

  const { data, isLoading, error } = useQuery({
    enabled: !!coachId,
    queryKey: ['reports', coachId],
    queryFn: () => getReport({ coachId: Number(coachId) }),
  });

  const allReports = data?.data || [];
  const studentReports = allReports.filter(
    (report) => report.studentProfileId === student?.studentId
  );

  const posePerformance = Object.values(
    studentReports.reduce((acc, report) => {
      if (!acc[report.asanaName]) {
        acc[report.asanaName] = { pose: report.asanaName, total: 0, count: 0 };
      }
      acc[report.asanaName].total += report.performance;
      acc[report.asanaName].count += 1;
      return acc;
    }, {})
  ).map(({ pose, total, count }) => ({
    pose,
    accuracy: (total / count).toFixed(1),
  }));

  const sessionHistory = [...studentReports]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .map((report, idx) => {
      let formattedDate = '';
      if (report.date) {
        const d = new Date(report.date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        formattedDate = `${day}-${month}-${year}`;
      }
      return {
        date: formattedDate,
        session: `Session ${idx + 1}`,
        asanaName: report.asanaName,
        accuracy: report.performance,
      };
    });

  const sortedSessionHistory = [...sessionHistory].sort((a, b) => {
    if (sortConfig.key === 'date') {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    if (sortConfig.key === 'accuracy') {
      return sortConfig.direction === 'asc' ? a.accuracy - b.accuracy : b.accuracy - a.accuracy;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const accuracyTrendData = Object.values(
    studentReports.reduce((acc, report) => {
      let formattedDate = '';
      if (report.date) {
        const d = new Date(report.date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        formattedDate = `${day}-${month}-${year}`;
      }
      if (!acc[formattedDate]) {
        acc[formattedDate] = { date: formattedDate, total: 0, count: 0 };
      }
      acc[formattedDate].total += report.performance;
      acc[formattedDate].count += 1;
      return acc;
    }, {})
  ).map(({ date, total, count }) => ({
    date,
    accuracy: (total / count).toFixed(1),
  })).sort((a, b) => {
    const [dayA, monthA, yearA] = a.date.split('-').map(Number);
    const [dayB, monthB, yearB] = b.date.split('-').map(Number);
    return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
  });

  if (!student) {
    return (
      <Box className="max-w-7xl mx-auto p-6 bg-gray-100 min-h-screen">
        <Typography variant="h6" color="error">
          No student data available.
        </Typography>
        <Button variant="outlined" startIcon={<CaretLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box className="max-w-7xl mx-auto p-6 bg-gray-100 min-h-screen pb-10">
      {/* Header */}
      <Box className="flex justify-between items-center mb-8 sticky top-0 bg-gray-100 z-10 py-4 shadow-sm">
        <Typography variant="h4" className="font-extrabold text-gray-900" mb={1.5}>
          Student Profile
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CaretLeftOutlined />}
          onClick={() => navigate(-1)}
          className="rounded-lg border-teal-600 text-teal-600 hover:bg-teal-50"
        >
          Back
        </Button>
      </Box>

      {/* Profile Card */}
      <Card
        className="rounded-3xl border-none shadow-xl overflow-hidden"
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)',
          color: 'white',
          mt: 3,
          mb: 6, 
        }}
      >
        <CardContent className="flex flex-col gap-8 p-8">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center" gap={4}>
            <Avatar
              alt={student.studentName}
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(student.studentName)}&background=fff&color=0f766e&bold=true`}
              className="w-32 h-32 sm:w-36 sm:h-36 shadow-md ring-4 ring-white"
            />
            <Box className="flex-1 w-full text-center sm:text-left">
              <Typography variant="h4" className="font-extrabold text-white">
                {student.studentName}
              </Typography>
              <Box className="inline-block mt-2 px-3 py-1 bg-white text-teal-700 rounded-full text-xs font-semibold shadow-sm">
                ID: {student.studentId}
              </Box>
            </Box>
          </Box>

          <Divider className="!border-white/30" />

          <Grid container spacing={3} alignItems="center" mt={0.5}>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                <MailOutlined style={{ fontSize: '18px' }} />
                <Typography variant="body2" noWrap>{student.studentEmail}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                <PhoneOutlined style={{ fontSize: '18px' }} />
                <Typography variant="body2" noWrap>{student.studentContact}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                <CalendarOutlined style={{ fontSize: '18px' }} />
                <Typography variant="body2" noWrap>Joined: {student.registrationDate}</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={5}>
        {/* Accuracy Trend */}
        <Grid item xs={12} mt={2}>
          <Card className="rounded-3xl shadow-2xl">
            <CardContent className="p-8">
              <Typography variant="h5" className="font-extrabold text-gray-900 mb-4">
                Accuracy Trend
              </Typography>
              <ReactApexChart
                options={{
                  chart: {
                    id: 'accuracy-trend',
                    type: 'area',
                    toolbar: { show: true },
                    height: 400
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: (val) => `${val}%`
                  },
                  stroke: { curve: 'smooth', width: 3 },
                  fill: {
                    type: 'gradient',
                    gradient: { shade: 'light', type: 'vertical', opacityFrom: 0.85, opacityTo: 0.15 }
                  },
                  xaxis: {
                    categories: accuracyTrendData.map((d) => d.date),
                    title: { text: 'Date' }
                  },
                  yaxis: {
                    min: 0,
                    max: 100,
                    tickAmount: 5,
                    labels: { formatter: (val) => `${val}%` },
                    title: { text: 'Accuracy (%)' }
                  },
                  tooltip: { y: { formatter: (val) => `${val}%` } },
                  grid: { strokeDashArray: 4 },
                  colors: ['#0f766e']
                }}
                series={[{ name: 'Accuracy', data: accuracyTrendData.map((d) => d.accuracy) }]}
                type="area"
                height={400}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Session History */}
        <Grid item xs={12} md={6} mt={2}>
          <Card className="rounded-3xl shadow-2xl">
            <CardContent className="p-8">
              <Box className="flex justify-between items-center mb-4">
                <Typography variant="h5" className="font-extrabold text-gray-900">
                  Session History
                </Typography>
                <Box className="flex gap-2">
                  <IconButton onClick={() => handleSort('date')} title="Sort by Date">
                    {sortConfig.key === 'date' && sortConfig.direction === 'asc' ? (
                      <SortAscendingOutlined />
                    ) : (
                      <SortDescendingOutlined />
                    )}
                  </IconButton>
                  <IconButton onClick={() => handleSort('accuracy')} title="Sort by Accuracy">
                    {sortConfig.key === 'accuracy' && sortConfig.direction === 'asc' ? (
                      <SortAscendingOutlined />
                    ) : (
                      <SortDescendingOutlined />
                    )}
                  </IconButton>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Session Name</TableCell>
                      <TableCell>Pose Name</TableCell>
                      <TableCell>Accuracy (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSessionHistory.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.session}</TableCell>
                        <TableCell>{row.asanaName}</TableCell>
                        <TableCell>{row.accuracy}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Pose-wise Performance */}
        <Grid item xs={12} md={6} mt={2}>
          <Card className="rounded-3xl shadow-2xl">
            <CardContent className="p-8">
              <Typography variant="h5" className="font-extrabold text-gray-900 mb-4">
                Pose-wise Performance
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Asana Name</TableCell>
                      <TableCell>Average Accuracy (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {posePerformance.length > 0 ? (
                      posePerformance.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.pose}</TableCell>
                          <TableCell>{row.accuracy}%</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2}>No pose data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentProfile;
